import { useRive, useStateMachineInput, Layout, Fit, Alignment } from '@rive-app/react-canvas';
import { useEffect, useRef, useCallback } from 'react';

interface RiveDumpsterProps {
  width?: number;
  height?: number;
  isOpen?: boolean;
  onOpenComplete?: () => void;
}

export function RiveDumpster({
  width = 650,
  height = 600,
  isOpen = false,
  onOpenComplete
}: RiveDumpsterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Track if component is mounted to prevent accessing deleted rive instance
  const isMountedRef = useRef(true);

  // The hover animation is bound to cursor automatically in the Rive file
  // We only need to manually control the isOpen trigger
  const { rive, RiveComponent } = useRive({
    src: '/dumpster.riv',
    stateMachines: 'State Machine 1', // Load single state machine with both hover and isOpen
    autoplay: true,
    layout: new Layout({
      fit: Fit.Contain,
      alignment: Alignment.Center,
    }),
  });

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Safe wrapper to check if rive is valid before operations
  const safeRivePlay = useCallback((animationName: string) => {
    if (!isMountedRef.current || !rive) return false;
    try {
      rive.play(animationName);
      return true;
    } catch (error) {
      // Rive instance was cleaned up, ignore the error
      console.debug('[RiveDumpster] Rive play ignored (unmounted):', animationName);
      return false;
    }
  }, [rive]);

  // Debug: Log when Rive instance is ready
  useEffect(() => {
    if (rive) {
      console.log('✅ Rive instance loaded successfully');
      console.log('📋 Available state machines:', rive.stateMachineNames);
      console.log('📋 Available animations:', rive.animationNames);
      
      // Try all available state machines
      rive.stateMachineNames.forEach((smName: string) => {
        console.log(`🎮 "${smName}" inputs:`, rive.stateMachineInputs(smName));
      });
      
      console.log('🎯 Note: Hover animation should be automatic if bound to cursor in Rive file');
    }
  }, [rive]);

  // Get the isOpen trigger input from the state machine
  const isOpenInput = useStateMachineInput(
    rive,
    'State Machine 1',
    'isOpen'
  );

  // Debug: Log when isOpen input is found
  useEffect(() => {
    if (isOpenInput) {
      console.log('🎯 isOpen input found:', isOpenInput);
    } else if (rive) {
      console.warn('⚠️ isOpen input not found! Available inputs:', rive.stateMachineInputs('State Machine 1'));
    }
  }, [isOpenInput, rive]);

  // Fire the opening animation when the prop changes
  useEffect(() => {
    if (!rive || !isOpen) return;

    const timeouts: NodeJS.Timeout[] = [];

    console.log('🎬 Playing lid opening animation...');

    // Play the lid opening animation using safe wrapper
    if (safeRivePlay('IidOpen')) {
      console.log('✅ Playing "IidOpen" (lid opening) animation');
    }

    // After opening animation, transition to opened state
    const transitionTimeout = setTimeout(() => {
      // Check if component is still mounted before calling play
      if (isMountedRef.current && safeRivePlay('IidOpened')) {
        console.log('✅ Transitioned to "IidOpened" (lid opened) state');
      }
    }, 800); // Adjust timing based on animation duration
    timeouts.push(transitionTimeout);

    // If a callback is provided, call it after animation completes
    if (onOpenComplete) {
      const callbackTimeout = setTimeout(() => {
        // Only call callback if still mounted
        if (isMountedRef.current) {
          onOpenComplete();
        }
      }, 1000);
      timeouts.push(callbackTimeout);
    }

    // Cleanup timeouts on unmount to prevent accessing deleted rive instance
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [isOpen, rive, onOpenComplete, safeRivePlay]);

  // Manual hover handlers as fallback if Rive's automatic hover doesn't work
  // Disable hover when chest is open
  const handleMouseEnter = useCallback(() => {
    if (isOpen) return; // Don't trigger hover when chest is open
    if (rive?.animationNames?.includes('hover In')) {
      console.log('🎯 Manual hover In trigger');
      safeRivePlay('hover In');
    }
  }, [isOpen, rive, safeRivePlay]);

  const handleMouseLeave = useCallback(() => {
    if (isOpen) return; // Don't trigger hover when chest is open
    if (rive?.animationNames?.includes('hover Out')) {
      console.log('👋 Manual hover Out trigger');
      safeRivePlay('hover Out');
    }
  }, [isOpen, rive, safeRivePlay]);

  return (
    <div 
      ref={containerRef}
      style={{ 
        width: `${width}px`, 
        height: `${height}px`,
        position: 'relative',
        pointerEvents: 'none', // Allow clicks to pass through the container
      }}
    >
      {/* Hover detection zone - much smaller area centered on dumpster lid */}
      <div
        style={{
          position: 'absolute',
          width: '180px', // Width for lid area
          height: '300px', // Extended height for more bottom coverage
          top: '54%', // Moved down slightly more to reduce top region
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'auto', // Only this zone detects hover
          zIndex: 1,
          // Uncomment to see the hover zone (for debugging):
          // backgroundColor: 'rgba(255, 0, 0, 0.2)',
          // border: '2px solid red',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      <RiveComponent 
        style={{ 
          width: '100%', 
          height: '100%',
          display: 'block',
          pointerEvents: 'none', // Rive canvas doesn't block clicks
          position: 'relative',
          zIndex: 0,
        }}
      />
    </div>
  );
}

